/**
 * CleanPreview Component
 * ======================
 * Shows a preview of cleanup changes before applying them.
 * 
 * Features:
 * - Green highlight: Missing data being filled in
 * - Yellow highlight: Data being changed from one value to another
 * - Red highlight + strikethrough: Racers being removed (with checkboxes)
 * - Accept/Decline buttons
 */

import React, { useState, useEffect } from 'react';
import './CleanPreview.css';

// Fields to display in the preview table
// Order matches main data view: Rank, Status, Bib, Name, Nation, YOB, Club, IDs, Points, Time
const BASE_PREVIEW_FIELDS = [
  'rank', 'status', 'bib', 'firstname', 'lastname', 'Country', 'YOB', 'Club',
  'USSAcode', 'FIScode', 'NRLpoints', 'FISpoints', 'time'
];

// Sprint-specific fields
const SPRINT_FIELDS = ['Highest Level'];

function CleanPreview({
  isOpen,
  onClose,
  originalRacers,  // Original data before cleanup
  cleanupResult,   // { validRacers, removedRacers, changes }
  type,            // 'fis' or 'ussa'
  onAccept,        // Called with final racers when accepted
  onDecline,       // Called when declined
}) {
  // Track which removals to apply (checkboxes)
  const [removalsToApply, setRemovalsToApply] = useState({});
  
  // Initialize checkboxes - all removals selected by default
  useEffect(() => {
    if (cleanupResult?.removedRacers) {
      const initialState = {};
      cleanupResult.removedRacers.forEach((item, idx) => {
        initialState[idx] = true; // Checked by default
      });
      setRemovalsToApply(initialState);
    }
  }, [cleanupResult]);
  
  if (!isOpen || !cleanupResult) return null;
  
  const { validRacers = [], removedRacers = [], changes = [] } = cleanupResult;
  
  // Create a map of changes by racer key for quick lookup
  const changesMap = new Map();
  changes.forEach(change => {
    const key = change.fisCode || change.ussaCode;
    if (!changesMap.has(key)) {
      changesMap.set(key, []);
    }
    changesMap.get(key).push(change);
  });
  
  // Field name mappings - must match DataWorkflow's getRacerField mappings
  const FIELD_MAPPINGS = {
    'rank': ['rank', 'Rank', 'RANK', 'place', 'Place', 'position', 'Position'],
    'bib': ['bib', 'Bib', 'BIB', 'Racer #', 'racer number', 'bib number', 'num', 'Round'],
    'firstname': ['firstname', 'FirstName', 'firstName', 'first_name', 'First Name', 'first name', 'given name', 'FIRSTNAME'],
    'lastname': ['lastname', 'LastName', 'lastName', 'last_name', 'Last Name', 'last name', 'surname', 'family name', 'LASTNAME'],
    'FIScode': ['FIScode', 'fisCode', 'FIS Licence', 'FISLicence', 'FIS License', 'FISLicense', 'FIS Code', 'FISCode', 'FIS #', 'FIS', 'fis_licence', 'fis_license', 'FIS Racing License', 'FIS Racing Licence', 'FISRacingLicense', 'FISRacingLicence'],
    'USSAcode': ['USSAcode', 'ussaCode', 'USSA Number', 'USSANumber', 'USSA #', 'USSA', 'USSA Racing License', 'USSARacingLicense', 'USSA Racing Licence', 'USSARacingLicence', 'USSA License', 'USSALicense', 'USSS #', 'USSS', 'USSS Number', 'NC License', 'NC Licence', 'NCLicense', 'NCLicence', 'ussa_number'],
    'Country': ['Country', 'country', 'COUNTRY', 'nation', 'Nation', 'NAT', 'Nat', 'nationcode'],
    'YOB': ['YOB', 'yob', 'Birth Year', 'BirthYear', 'Year of Birth', 'YearOfBirth', 'year_of_birth'],
    'time': ['time', 'Time', 'TIME', 'Finish', 'finish', 'finish time', 'final time', 'Total Time', 'totalTime'],
    'FISpoints': ['FISpoints', 'fisPoints', 'FIS Points', 'FIS Distance', 'FIS (DI or SP) Points', 'fis_points'],
    'NRLpoints': ['NRLpoints', 'nrlPoints', 'ussaPoints', 'USSA Distance', 'USSADistance', 'USSA Points', 'USSAPoints', 'NRL Points'],
    'status': ['status', 'Status', 'STATUS', 'result status', 'finish status'],
    'Club': ['Club', 'club', 'CLUB', 'Team', 'team'],
    // Sprint heats fields
    'Highest Level': ['Highest Level', 'highestLevel', 'HighestLevel', 'highest level', 'Level', 'level'],
  };
  
  // Helper to get display value for a field
  const getFieldValue = (racer, field) => {
    if (!racer) return '';
    const keys = FIELD_MAPPINGS[field] || [field];
    for (const key of keys) {
      if (racer[key] !== undefined && racer[key] !== null && racer[key] !== '') {
        return racer[key];
      }
    }
    return '';
  };
  
  // Helper to get racer key - use USSAcode or FIScode, with proper field mapping
  const getRacerKey = (racer) => {
    if (!racer) return '';
    
    // Try USSA code first (for USSA cleanup)
    const ussaCode = getFieldValue(racer, 'USSAcode');
    if (ussaCode) return String(ussaCode).trim();
    
    // Try FIS code
    const fisCode = getFieldValue(racer, 'FIScode');
    if (fisCode) return String(fisCode).trim();
    
    // Fall back to name
    const firstName = getFieldValue(racer, 'firstname');
    const lastName = getFieldValue(racer, 'lastname');
    if (firstName || lastName) return `${firstName}-${lastName}`;
    
    // Last resort: bib number
    const bib = getFieldValue(racer, 'bib');
    if (bib) return `bib-${bib}`;
    
    return '';
  };
  
  // Determine cell style based on old vs new value
  const getCellStyle = (racer, field, originalRacer) => {
    const newValue = getFieldValue(racer, field);
    const oldValue = originalRacer ? getFieldValue(originalRacer, field) : null;
    
    // If we couldn't find the original racer, don't highlight anything
    // (this prevents the entire row from being green when key matching fails)
    if (!originalRacer) {
      return 'normal';
    }
    
    // Normalize values for comparison
    const newStr = newValue !== null && newValue !== undefined ? String(newValue).trim() : '';
    const oldStr = oldValue !== null && oldValue !== undefined ? String(oldValue).trim() : '';
    
    // Case-insensitive comparison - case changes (JOHN → John) are not worth highlighting
    const newLower = newStr.toLowerCase();
    const oldLower = oldStr.toLowerCase();
    
    // If values are the same (ignoring case), no highlight needed
    if (newLower === oldLower) {
      return 'normal';
    }
    
    // Check if this field has an explicit change record from cleanup
    const racerKey = getRacerKey(racer);
    const racerChanges = changesMap.get(racerKey) || [];
    const fieldChange = racerChanges.find(c => 
      c.field === field || 
      c.field === field.toLowerCase() ||
      (field === 'firstname' && c.field === 'firstName') ||
      (field === 'lastname' && c.field === 'lastName') ||
      (field === 'YOB' && c.field === 'yob') ||
      (field === 'Country' && c.field === 'country') ||
      (field === 'FISpoints' && c.field === 'fisPoints') ||
      (field === 'NRLpoints' && (c.field === 'ussaPoints' || c.field === 'nrlPoints'))
    );
    
    if (fieldChange) {
      // Check if it's just a case change - don't highlight
      const changeOldLower = fieldChange.oldValue ? String(fieldChange.oldValue).trim().toLowerCase() : '';
      const changeNewLower = fieldChange.newValue ? String(fieldChange.newValue).trim().toLowerCase() : '';
      if (changeOldLower === changeNewLower) {
        return 'normal'; // Just a case change, not worth highlighting
      }
      
      // Was there an old value?
      const hadOldValue = fieldChange.oldValue !== null && 
                          fieldChange.oldValue !== undefined && 
                          String(fieldChange.oldValue).trim() !== '';
      if (hadOldValue) {
        return 'changed'; // Yellow - value changed from one to another
      } else {
        return 'filled'; // Green - missing data filled in
      }
    }
    
    // No explicit change record - compare values directly
    // Only show green if old value was genuinely empty and new is not
    if (newStr !== '' && oldStr === '') {
      return 'filled'; // Green - new data filled in
    }
    
    // Show yellow if values are actually different (not just case)
    // Already checked case-insensitive equality above, so if we get here values differ
    if (oldStr !== '') {
      return 'changed'; // Yellow - value changed
    }
    
    return 'normal';
  };
  
  // Handle removal checkbox toggle
  const toggleRemoval = (idx) => {
    setRemovalsToApply(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  
  // Handle Accept
  const handleAccept = () => {
    // Start with valid racers
    let finalRacers = [...validRacers];
    
    // Add back any unchecked removals
    removedRacers.forEach((item, idx) => {
      if (!removalsToApply[idx]) {
        // User unchecked this removal, add racer back
        finalRacers.push(item.racer);
      }
    });
    
    // Sort by rank/time
    finalRacers.sort((a, b) => {
      const rankA = parseInt(getFieldValue(a, 'rank')) || 999;
      const rankB = parseInt(getFieldValue(b, 'rank')) || 999;
      return rankA - rankB;
    });
    
    onAccept(finalRacers);
  };
  
  // Handle Decline
  const handleDecline = () => {
    onDecline();
  };
  
  const label = type === 'fis' ? 'FIS' : 'USSA';
  
  // Check if this is a sprint heats file (has Highest Level data)
  const hasSprintData = originalRacers?.some(r => 
    r['Highest Level'] || r.highestLevel || r.HighestLevel
  ) || validRacers?.some(r => 
    r['Highest Level'] || r.highestLevel || r.HighestLevel
  );
  
  // Build the preview fields list, adding sprint fields if present
  const PREVIEW_FIELDS = hasSprintData 
    ? [...BASE_PREVIEW_FIELDS, ...SPRINT_FIELDS]
    : BASE_PREVIEW_FIELDS;
  
  // Determine which columns are relevant based on type
  const relevantFields = PREVIEW_FIELDS.filter(field => {
    if (type === 'fis') {
      // FIS mode: exclude USSA-specific fields
      return field !== 'NRLpoints' && field !== 'USSAcode';
    }
    // USSA mode: show all fields (includes both FIS and USSA data)
    return true;
  });
  
  // Build a combined list maintaining original order
  // Map original racers to their cleanup status (valid, removed, or unchanged)
  const buildDisplayList = () => {
    // Create lookup maps for quick access
    const removedMap = new Map();
    removedRacers.forEach((item, idx) => {
      const key = getRacerKey(item.racer);
      removedMap.set(key, { ...item, removedIndex: idx });
    });
    
    const validMap = new Map();
    validRacers.forEach((racer) => {
      const key = getRacerKey(racer);
      validMap.set(key, racer);
    });
    
    // Build display list in original order
    const displayList = [];
    originalRacers?.forEach((originalRacer, originalIndex) => {
      const key = getRacerKey(originalRacer);
      
      if (removedMap.has(key)) {
        // This racer was removed
        const removedInfo = removedMap.get(key);
        displayList.push({
          type: 'removed',
          racer: removedInfo.racer,
          reason: removedInfo.reason,
          removedIndex: removedInfo.removedIndex,
          originalIndex,
        });
      } else if (validMap.has(key)) {
        // This racer is valid (possibly with changes)
        displayList.push({
          type: 'valid',
          racer: validMap.get(key),
          originalRacer,
          originalIndex,
        });
      }
    });
    
    return displayList;
  };
  
  const displayList = buildDisplayList();
  
  return (
    <div className="clean-preview-overlay" onClick={onClose}>
      <div className="clean-preview-modal" onClick={e => e.stopPropagation()}>
        <h2>{label} Cleanup Preview</h2>
        
        {/* Summary */}
        <div className="clean-preview-summary">
          <span className="summary-valid">✓ {validRacers.length} valid racers</span>
          <span className="summary-removed">✗ {removedRacers.length} removed</span>
          <span className="summary-changes">📝 {changes.length} changes</span>
        </div>
        
        {/* Legend */}
        <div className="clean-preview-legend">
          <span className="legend-item">
            <span className="legend-color filled"></span> Data filled in
          </span>
          <span className="legend-item">
            <span className="legend-color changed"></span> Data changed
          </span>
          <span className="legend-item">
            <span className="legend-color removed"></span> Removed (uncheck to keep)
          </span>
        </div>
        
        {/* Table */}
        <div className="clean-preview-table-wrapper">
          <table className="clean-preview-table">
            <thead>
              <tr>
                <th></th> {/* Checkbox column */}
                {relevantFields.map(field => (
                  <th key={field}>{field}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Display all racers in original order */}
              {displayList.map((item, idx) => {
                if (item.type === 'removed') {
                  // Removed racer row
                  const isChecked = removalsToApply[item.removedIndex] ?? true;
                  return (
                    <tr key={`row-${idx}`} className={`removed-row ${!isChecked ? 'unchecked' : ''}`}>
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleRemoval(item.removedIndex)}
                          title={isChecked ? 'Uncheck to keep this racer' : 'Check to remove this racer'}
                        />
                      </td>
                      {relevantFields.map(field => (
                        <td key={field} className="removed-cell" title={item.reason}>
                          {getFieldValue(item.racer, field)}
                        </td>
                      ))}
                    </tr>
                  );
                } else {
                  // Valid racer row
                  const racer = item.racer;
                  const originalRacer = item.originalRacer;
                  
                  return (
                    <tr key={`row-${idx}`} className="valid-row">
                      <td className="checkbox-cell">
                        {/* No checkbox for valid racers */}
                      </td>
                      {relevantFields.map(field => {
                        const cellStyle = getCellStyle(racer, field, originalRacer);
                        const originalValue = originalRacer ? getFieldValue(originalRacer, field) : '';
                        const newValue = getFieldValue(racer, field);
                        
                        return (
                          <td 
                            key={field} 
                            className={`cell-${cellStyle}`}
                            title={cellStyle !== 'normal' ? `Was: ${originalValue || '(empty)'}` : ''}
                          >
                            {newValue}
                          </td>
                        );
                      })}
                    </tr>
                  );
                }
              })}
            </tbody>
          </table>
        </div>
        
        {/* Buttons */}
        <div className="clean-preview-buttons">
          <button className="accept-btn" onClick={handleAccept}>Accept Changes</button>
          <button className="decline-btn" onClick={handleDecline}>Decline</button>
        </div>
      </div>
    </div>
  );
}

export default CleanPreview;

